import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AddBeneficiaryModal.css';

interface AddBeneficiaryModalProps {
  onClose: () => void;
  onAdd: (beneficiary: any) => void;
}

const AddBeneficiaryModal: React.FC<AddBeneficiaryModalProps> = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    accountNumber: '',
    bankName: '',
    trustLevel: 'public' as 'public' | 'semi-private' | 'anonymous',
    uniqueCode: ''
  });
  const [activeTab, setActiveTab] = useState<'manual' | 'code'>('manual');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const beneficiaryData = {
        ...formData,
        discoveryMethod: activeTab === 'manual' ? 'manual-entry' : 'unique-code'
      };
      
      await onAdd(beneficiaryData);
    } catch (error) {
      console.error('Error adding beneficiary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Add New Beneficiary</h2>
          <motion.button
            className="close-btn"
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </motion.button>
        </div>

        <div className="tab-navigation">
          <motion.button
            className={`tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Manual Entry
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === 'code' ? 'active' : ''}`}
            onClick={() => setActiveTab('code')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Unique Code
          </motion.button>
        </div>

        <form onSubmit={handleSubmit} className="beneficiary-form">
          <AnimatePresence mode="wait">
            {activeTab === 'manual' ? (
              <motion.div
                key="manual"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="form-content"
              >
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter beneficiary name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="accountNumber">Account Number</label>
                    <input
                      type="text"
                      id="accountNumber"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      placeholder="1234567890"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bankName">Bank Name</label>
                    <input
                      type="text"
                      id="bankName"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="Bank name"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="trustLevel">Trust Level</label>
                  <select
                    id="trustLevel"
                    name="trustLevel"
                    value={formData.trustLevel}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="public">Public (Openly discoverable)</option>
                    <option value="semi-private">Semi-Private (Verification required)</option>
                    <option value="anonymous">Anonymous (Not discoverable)</option>
                  </select>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="code"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="form-content"
              >
                <div className="form-group">
                  <label htmlFor="uniqueCode">Unique Code or Reference</label>
                  <input
                    type="text"
                    id="uniqueCode"
                    name="uniqueCode"
                    value={formData.uniqueCode}
                    onChange={handleInputChange}
                    placeholder="Enter unique code or reference"
                    required
                  />
                  <p className="form-hint">
                    Enter a unique code provided by the beneficiary to add them securely
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="name">Display Name (Optional)</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="How you'd like to identify this beneficiary"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="form-actions">
            <motion.button
              type="button"
              className="btn secondary"
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            
            <motion.button
              type="submit"
              className="btn primary"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <motion.div
                  className="loading-spinner"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                'Add Beneficiary'
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AddBeneficiaryModal;
